import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback renderer. Receives the error and a reset fn. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-level error boundary. Catches render/lifecycle errors anywhere in the
 * tree below it and shows a recovery card instead of an unmounted (blank)
 * page. Without this, any uncaught render error white-screens the whole SPA.
 *
 * React error boundaries only catch errors during rendering, in lifecycle
 * methods, and in constructors of the tree below them — not event handlers or
 * async code. Those paths surface through toasts / query error states instead.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep a console record for support/debugging. A remote logger can hook
    // in here later without changing the recovery UX.
    console.error('[ErrorBoundary] uncaught render error:', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background px-6 text-center"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-7 w-7" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">Đã xảy ra lỗi</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            Giao diện gặp sự cố không mong muốn. Bạn có thể thử lại hoặc quay về trang chủ.
            Nếu lỗi tiếp diễn, vui lòng liên hệ hỗ trợ.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Tải lại trang
          </Button>
          <Button variant="outline" asChild>
            <a href="/">
              <Home className="h-4 w-4" aria-hidden="true" />
              Về trang chủ
            </a>
          </Button>
        </div>
        {import.meta.env.DEV ? (
          <pre className="mt-2 max-w-xl overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
        ) : null}
      </div>
    );
  }
}

export default ErrorBoundary;
